import type { ClientThreadMessage } from "../../services/clientSmsService";

/**
 * THREAD STREAM MODEL — the grouping rules, separated from the rendering.
 *
 * Extracted deliberately: the grouping is where the behaviour lives, and a
 * component test that only asserts "some rows rendered" cannot tell a correct
 * grouping from a broken one. Here the rules are directly assertable.
 *
 * THE RULES
 *
 * 1 · A human message opens a GROUP. Consecutive messages continue it while
 *     all of: same direction, still a plain message, within GROUP_WINDOW_MS,
 *     and same calendar day. Slack's own rule, minus the multi-sender case —
 *     an SMS thread has exactly two parties.
 *
 * 2 · SYSTEM output never joins a human group. `media_bounce` and `help_reply`
 *     are things the SYSTEM said on our behalf; rendering them inside the
 *     operator's run of messages implies a person typed them. They become
 *     their own single-item entries and render as quiet event rows.
 *
 * 3 · A DAY BOUNDARY always breaks a group, even inside the time window — a
 *     group header carries one timestamp, and one timestamp cannot honestly
 *     span two days.
 *
 * The window is 5 minutes: long enough that a burst of texts reads as one
 * turn, short enough that "this morning" and "this afternoon" don't collapse
 * into a single block with one misleading time.
 */

export const GROUP_WINDOW_MS = 5 * 60 * 1000;

export type StreamEntryKind = "group" | "system";

export interface StreamEntry {
  key: string;
  kind: StreamEntryKind;
  /** "in" | "out" — for a system entry, the direction it was sent on. */
  direction: string;
  messages: ClientThreadMessage[];
  /** Day label for the divider that precedes this entry, when it starts one. */
  dayLabel?: string;
}

function parseTime(raw?: string | null): number | null {
  if (!raw) return null;
  const t = Date.parse(raw);
  return isNaN(t) ? null : t;
}

/** Calendar-day key in the viewer's locale, so "day" means the operator's day. */
export function dayKey(raw?: string | null): string {
  const t = parseTime(raw);
  if (t === null) return "";
  return new Date(t).toDateString();
}

export function dayLabelFor(raw?: string | null): string {
  const t = parseTime(raw);
  if (t === null) return "";
  const d = new Date(t);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86400000);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function isSystemKind(kind?: string | null): boolean {
  return !!kind && kind !== "message";
}

/**
 * Fold a flat message list into the entries the stream renders.
 *
 * Order is preserved exactly — this only groups; it never sorts, drops, or
 * dedupes. A message that goes in comes out, in the same position.
 */
export function buildStream(messages: ClientThreadMessage[]): StreamEntry[] {
  const entries: StreamEntry[] = [];
  let lastDay = "";

  for (const m of messages) {
    const thisDay = dayKey(m.created_at);
    const startsDay = !!thisDay && thisDay !== lastDay;
    if (startsDay) lastDay = thisDay;

    const system = isSystemKind(m.kind);
    const prev = entries[entries.length - 1];

    const continues =
      !!prev &&
      !system &&
      prev.kind === "group" &&
      prev.direction === m.direction &&
      !startsDay &&
      (() => {
        const last = prev.messages[prev.messages.length - 1];
        const a = parseTime(last?.created_at);
        const b = parseTime(m.created_at);
        // Missing timestamps must not silently glue unrelated messages
        // together — without a time we cannot prove they belong in one turn.
        if (a === null || b === null) return false;
        return b - a <= GROUP_WINDOW_MS;
      })();

    if (continues) {
      prev.messages.push(m);
      continue;
    }

    entries.push({
      key: m.id,
      kind: system ? "system" : "group",
      direction: m.direction,
      messages: [m],
      dayLabel: startsDay ? dayLabelFor(m.created_at) : undefined,
    });
  }

  return entries;
}

/**
 * STALE-SENDING RULE (PKT: eleven-hour "Sending…", observed live 2026-08-07).
 *
 * An outbound row can be persisted with a pending status and no twilio_sid —
 * Twilio was never called, so the /sms/status webhook (which matches on
 * twilio_sid) can never deliver a terminal state. The row is not in flight;
 * it is orphaned, and rendering "Sending…" forever tells the operator a
 * message is on its way to a client when it is not.
 *
 * The threshold is 15 minutes: Twilio's queued→terminal transitions complete
 * in seconds-to-minutes, so a quarter hour is far past any legitimate flight
 * time while still impossible to hit during normal optimistic-send rendering.
 *
 * A pending message with NO timestamp is NOT stale: without a time we cannot
 * prove staleness (same principle as buildStream's missing-timestamp rule),
 * and the false alarm here — telling an operator a live send failed — is the
 * exact defect this rule exists to remove, in the other direction.
 */
export const STALE_SENDING_MS = 15 * 60 * 1000;

/**
 * The set of statuses that mean "no carrier handoff has been confirmed yet".
 * `accepted` and `pending` are not in this app's DeliveryStatus union, but the
 * insert path persists Twilio's create-response status VERBATIM — and a
 * messaging-service send (which this account uses) returns `accepted`. `sent`
 * is deliberately EXCLUDED: it is Twilio's statement that the carrier took the
 * message, so an old `sent` row is honest, not stale (review #378 item 3).
 */
export function isPendingStatus(status?: string | null): boolean {
  return (
    status === "queued" ||
    status === "accepted" ||
    status === "pending" ||
    status === "sending"
  );
}

export function isStaleSending(
  m: { status?: string | null; created_at?: string | null },
  nowMs: number
): boolean {
  if (!isPendingStatus(m.status)) return false;
  const t = parseTime(m.created_at ?? null);
  if (t === null) return false;
  return nowMs - t > STALE_SENDING_MS;
}
